/*
 * ExecutiveOverview.js — Phase 4 one-viewport executive dashboard.
 *
 * Layout follows the approved spec (see /app/memory/PERFORMANCE_REDESIGN.md §4):
 *   ┌─────────────────────────────────────────────────────────┐
 *   │  BIG NUMBER (Revenue)   +Δ%   ·   TREND SPARKLINE       │
 *   │  Executive Summary (2-line Claude synthesis)            │
 *   │  KPI STRIP × 4 (Orders · AOV · ROAS · Spend)            │
 *   │  🎯 Top Opportunity  │  ⚠️ Top Risk                     │
 *   │  ✨ Recommended Action                                   │
 *   └─────────────────────────────────────────────────────────┘
 *
 * Everything below is the existing "detailed hero + spend/ROAS strip"
 * collapsed by default under a "View Full Analytics" toggle.
 */
import React, { useMemo, useState } from "react";
import { TrendUp, TrendDown, Minus, Target, Warning, Sparkle, CaretDown, CaretUp } from "@phosphor-icons/react";
import SpendRevenueTrend from "./SpendRevenueTrend";

// ── Formatting helpers ──────────────────────────────────────────────────────
const fmtMoney = (v, ccy = "INR", compact = true) => {
  if (v == null || v === "" || isNaN(v)) return "—";
  try {
    if (compact && Math.abs(v) >= 100000) {
      const opts = { style: "currency", currency: ccy, maximumFractionDigits: 1, notation: "compact" };
      return new Intl.NumberFormat("en-IN", opts).format(v);
    }
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${ccy} ${Math.round(v).toLocaleString("en-IN")}`;
  }
};
const fmtNum = (v, { decimals = 0, suffix = "" } = {}) => {
  if (v == null || v === "" || isNaN(v)) return "—";
  return `${new Intl.NumberFormat("en-IN", { maximumFractionDigits: decimals }).format(v)}${suffix}`;
};
const fmtDelta = (pct) => {
  if (pct == null || pct === "" || isNaN(pct)) return null;
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}%`;
};

// ── Delta chip ──────────────────────────────────────────────────────────────
const DeltaChip = ({ pct, invertGood = false, testid }) => {
  if (pct == null || pct === "" || isNaN(pct)) {
    return <span className="text-[10px] text-white/40 uppercase tracking-wider" data-testid={testid}>no prev</span>;
  }
  const isPositive = pct >= 0;
  const isGood = invertGood ? !isPositive : isPositive;
  const Icon = pct === 0 ? Minus : isPositive ? TrendUp : TrendDown;
  const color = pct === 0 ? "text-white/50" : isGood ? "text-emerald-400" : "text-red-400";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${color}`} data-testid={testid}>
      <Icon size={11} weight="bold" /> {fmtDelta(pct)}
    </span>
  );
};

// ── Sparkline (tiny inline SVG) ─────────────────────────────────────────────
const Sparkline = ({ values, color = "#FFEB00", width = 120, height = 32 }) => {
  if (!values || values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = width / (values.length - 1);
  const points = values.map((v, i) => `${i * step},${height - ((v - min) / range) * height}`).join(" ");
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline points={points} fill="none" stroke={color} strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" />
      <polyline points={`0,${height} ${points} ${width},${height}`} fill={`${color}22`} stroke="none" />
    </svg>
  );
};

// ── KPI tile (Orders / AOV / ROAS / Spend) ──────────────────────────────────
const KpiTile = ({ label, value, delta, testid, invertGood = false }) => (
  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-[10px] text-white/50 uppercase tracking-wider">{label}</p>
      <DeltaChip pct={delta} invertGood={invertGood} testid={`${testid}-delta`} />
    </div>
    <p className="text-lg font-semibold text-white mt-1 leading-tight" data-testid={`${testid}-value`}>{value}</p>
  </div>
);

// ── Insight card (Top Opportunity / Top Risk) ───────────────────────────────
const InsightCard = ({ tone, icon: Icon, label, insight, onClick, testid }) => {
  const palette = {
    opportunity: {
      border: "border-emerald-500/30",
      bg: "bg-gradient-to-br from-emerald-500/10 via-transparent to-transparent",
      accent: "text-emerald-300",
      dot: "bg-emerald-400",
    },
    risk: {
      border: "border-red-500/30",
      bg: "bg-gradient-to-br from-red-500/10 via-transparent to-transparent",
      accent: "text-red-300",
      dot: "bg-red-400",
    },
  }[tone];

  const title = insight?.title || insight?.headline || "";
  const detail = insight?.detail || insight?.description || insight?.body || "";
  const impact = insight?.revenue_impact || insight?.impact || insight?.estimated_impact;
  const source = insight?.source || insight?.tag;

  if (!title && !detail) {
    return (
      <div className={`p-4 rounded-xl border ${palette.border} ${palette.bg}`} data-testid={testid}>
        <div className="flex items-center gap-2 mb-1.5">
          <Icon size={14} weight="fill" className={palette.accent} />
          <span className={`text-[10px] uppercase tracking-wider font-semibold ${palette.accent}`}>{label}</span>
        </div>
        <p className="text-[12px] text-white/50 italic">Not enough data for this window.</p>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left w-full p-4 rounded-xl border ${palette.border} ${palette.bg} transition hover:brightness-110 hover:border-white/30 cursor-pointer`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={14} weight="fill" className={palette.accent} />
        <span className={`text-[10px] uppercase tracking-wider font-semibold ${palette.accent}`}>{label}</span>
        {source && <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/10 text-white/50 uppercase">{source}</span>}
      </div>
      <p className="text-sm font-semibold text-white leading-tight mb-1">{title}</p>
      {detail && <p className="text-[11px] text-white/70 leading-relaxed line-clamp-3">{detail}</p>}
      {impact && (
        <p className="text-[11px] mt-2 font-medium">
          <span className="text-white/60">Est. impact: </span>
          <span className={palette.accent}>{impact}</span>
        </p>
      )}
      {onClick && (
        <p className="text-[10px] text-white/40 mt-2 uppercase tracking-wider">Click to view source →</p>
      )}
    </button>
  );
};

// ── Recommended Action strip ────────────────────────────────────────────────
const RecommendedAction = ({ recommendation, onClick }) => {
  if (!recommendation) return null;
  const title = recommendation?.title || "";
  const detail = recommendation?.detail || recommendation?.description || "";
  if (!title && !detail) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left w-full p-4 rounded-xl border border-[#FFEB00]/40 bg-gradient-to-r from-[#FFEB00]/10 via-[#FFEB00]/5 to-transparent hover:brightness-110 transition"
      data-testid="overview-recommended-action"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Sparkle size={14} weight="fill" className="text-[#FFEB00]" />
        <span className="text-[10px] uppercase tracking-wider font-semibold text-[#FFEB00]">Recommended action this week</span>
      </div>
      {title && <p className="text-sm font-semibold text-white leading-tight mb-1">{title}</p>}
      {detail && <p className="text-[12px] text-white/80 leading-relaxed">{detail}</p>}
    </button>
  );
};

// ── Executive Summary card ──────────────────────────────────────────────────
const ExecutiveSummaryCard = ({ summary, headline, confidence }) => {
  if (!summary && !headline) return null;
  return (
    <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10" data-testid="overview-exec-summary">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">Executive Summary</p>
        {confidence && (
          <span className={`text-[9px] px-1.5 py-0.5 rounded border uppercase tracking-wider ${
            confidence === "high" ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" :
            confidence === "low" ? "text-yellow-300 border-yellow-500/40 bg-yellow-500/10" :
            "text-white/60 border-white/20 bg-white/[0.04]"
          }`}>{confidence} confidence</span>
        )}
      </div>
      {headline && <p className="text-[13px] font-semibold text-white leading-snug mb-2">{headline}</p>}
      {summary && <p className="text-[12px] text-white/75 leading-relaxed">{summary}</p>}
    </div>
  );
};

// ── The big hero: revenue number + delta + sparkline ────────────────────────
const HeroNumber = ({ revenue, currency, delta, sparklineValues, clientName, dateRange }) => (
  <div className="p-5 rounded-xl bg-gradient-to-br from-white/[0.04] via-transparent to-transparent border border-white/10" data-testid="overview-hero-number">
    <div className="flex items-baseline justify-between flex-wrap gap-3">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <p className="text-[11px] uppercase tracking-wider text-white/50">{clientName || "Revenue"}</p>
          {dateRange && (
            <span className="text-[10px] text-white/40 uppercase tracking-wider">
              · {dateRange.start_date} → {dateRange.end_date}
            </span>
          )}
        </div>
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-4xl sm:text-5xl font-bold text-white leading-none tracking-tight" style={{ fontFamily: "Varela Round, sans-serif" }} data-testid="overview-hero-revenue">
            {revenue > 0 ? fmtMoney(revenue, currency) : "—"}
          </span>
          <DeltaChip pct={delta} testid="overview-hero-delta" />
        </div>
      </div>
      {sparklineValues && sparklineValues.length >= 2 && (
        <div className="opacity-80" data-testid="overview-hero-sparkline">
          <Sparkline values={sparklineValues} width={160} height={40} />
        </div>
      )}
    </div>
  </div>
);

// ── Main component ──────────────────────────────────────────────────────────
const ExecutiveOverview = ({
  clientId,
  clientName,
  currency,
  dateRange,
  kpi,
  intel,
  spendSummary,
  loadingIntel,
  onNavigateToTab,
  detailedHero, // the existing PerformanceHero — collapsed by default
}) => {
  const [showFullAnalytics, setShowFullAnalytics] = useState(false);

  // Extract KPI values (compatible with the /executive-kpi response shape)
  const revenue = useMemo(() => {
    if (!kpi) return 0;
    return kpi?.shopify?.revenue || kpi?.blended?.revenue || 0;
  }, [kpi]);

  const orders = kpi?.shopify?.orders || 0;
  const aov = kpi?.shopify?.aov || 0;
  const totalSpend = spendSummary?.total_spend || 0;
  const roas = totalSpend > 0 && revenue > 0 ? revenue / totalSpend : null;

  // Find kpi_deltas
  const deltaMap = useMemo(() => {
    const map = {};
    (intel?.kpi_deltas || []).forEach((d) => {
      if (d.metric) map[d.metric] = d;
    });
    return map;
  }, [intel]);

  const revenueDelta = deltaMap.revenue?.delta_pct;
  const ordersDelta = deltaMap.orders?.delta_pct;
  const aovDelta = deltaMap.aov?.delta_pct;

  // Sparkline from ga4 or blended timeseries (fallback: none)
  const sparklineValues = useMemo(() => {
    const series = kpi?.timeseries?.revenue || kpi?.blended?.timeseries || null;
    if (Array.isArray(series) && series.length >= 2) {
      return series.map((p) => Number(p?.value ?? p?.revenue ?? 0));
    }
    return null;
  }, [kpi]);

  return (
    <div className="space-y-4" data-testid="executive-overview">
      {/* Hero big number */}
      <HeroNumber
        revenue={revenue}
        currency={currency}
        delta={revenueDelta}
        sparklineValues={sparklineValues}
        clientName={clientName}
        dateRange={dateRange}
      />

      {/* Executive summary */}
      {loadingIntel ? (
        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 animate-pulse" data-testid="overview-summary-loading">
          <div className="h-3 bg-white/10 rounded w-24 mb-3" />
          <div className="h-4 bg-white/10 rounded w-3/4 mb-2" />
          <div className="h-3 bg-white/10 rounded w-full mb-1" />
          <div className="h-3 bg-white/10 rounded w-11/12" />
        </div>
      ) : (
        <ExecutiveSummaryCard
          summary={intel?.executive_summary}
          headline={intel?.headline}
          confidence={intel?.confidence}
        />
      )}

      {/* 4-KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="overview-kpi-strip">
        <KpiTile
          label="Revenue"
          value={revenue > 0 ? fmtMoney(revenue, currency) : "—"}
          delta={revenueDelta}
          testid="overview-kpi-revenue"
        />
        <KpiTile
          label="Orders"
          value={orders > 0 ? fmtNum(orders) : "—"}
          delta={ordersDelta}
          testid="overview-kpi-orders"
        />
        <KpiTile
          label="AOV"
          value={aov > 0 ? fmtMoney(aov, currency) : "—"}
          delta={aovDelta}
          testid="overview-kpi-aov"
        />
        <KpiTile
          label={roas !== null ? "ROAS" : "Spend"}
          value={roas !== null ? `${roas.toFixed(2)}×` : totalSpend > 0 ? fmtMoney(totalSpend, currency) : <span className="text-white/40 italic text-sm">Not set</span>}
          delta={null}
          testid="overview-kpi-roas"
        />
      </div>

      {/* Top Opportunity + Top Risk */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <InsightCard
          tone="opportunity"
          icon={Target}
          label="Top Opportunity"
          insight={intel?.opportunity}
          onClick={() => onNavigateToTab?.("ai-advisor")}
          testid="overview-opportunity"
        />
        <InsightCard
          tone="risk"
          icon={Warning}
          label="Top Risk"
          insight={intel?.risk}
          onClick={() => onNavigateToTab?.("ai-advisor")}
          testid="overview-risk"
        />
      </div>

      {/* Recommended Action */}
      <RecommendedAction
        recommendation={intel?.recommendation}
        onClick={() => onNavigateToTab?.("ai-advisor")}
      />

      {/* 6-month Spend vs Revenue trend */}
      {clientId && <SpendRevenueTrend clientId={clientId} currency={currency} months={6} />}

      {/* Toggle full analytics */}
      {detailedHero && (
        <div className="pt-2 border-t border-white/5">
          <button
            type="button"
            onClick={() => setShowFullAnalytics(v => !v)}
            className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded border border-white/15 text-white/70 hover:bg-white/[0.04] transition"
            data-testid="overview-toggle-full"
          >
            {showFullAnalytics ? <CaretUp size={12} /> : <CaretDown size={12} />}
            {showFullAnalytics ? "Hide full analytics" : "View full analytics"}
          </button>
          {showFullAnalytics && (
            <div className="mt-4" data-testid="overview-full-analytics">
              {detailedHero}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExecutiveOverview;
